import type { GridColumnVisibilityDefinition } from '../types';
import { isAvailableColumnFactory } from './isAvailableColumnFactory';

/**
 * Вставляет колонку сразу за последней из уже видимых, которая стоит раньше неё в `columnDefinitions`,
 * чтобы новая колонка попадала на своё место в гриде, а не в конец
 * @kind helper
 * @param visibleColumnIds - список видимых колонок, изменяется на месте
 * @param columnId - id вставляемой колонки
 * @param orderIndexByColumnId - позиции колонок в `columnDefinitions`
 * @param minInsertIndex - минимальная позиция вставки (чтобы не влезать в блок обязательных колонок)
 */
function insertByColumnDefinitionsOrder(
    visibleColumnIds: string[],
    columnId: string,
    orderIndexByColumnId: Map<string, number>,
    minInsertIndex: number
): void {
    const orderIndex = orderIndexByColumnId.get(columnId) ?? 0;

    let insertIndex = minInsertIndex;

    visibleColumnIds.forEach((visibleColumnId, index) => {
        const visibleOrderIndex = orderIndexByColumnId.get(visibleColumnId);

        if (visibleOrderIndex !== undefined && visibleOrderIndex < orderIndex) {
            insertIndex = Math.max(index + 1, minInsertIndex);
        }
    });

    visibleColumnIds.splice(insertIndex, 0, columnId);
}

/**
 * Приводит список видимых колонок к валидному состоянию:
 * оставляет только известные `columnId`, добавляет обязательные колонки и убирает дубликаты.
 *
 * Если передан `defaultVisibleColumnIdsToMergeIn`, то id из него, отсутствующие в запрошенном списке,
 * докидываются на свою позицию по порядку из `columnDefinitions` — сразу за последней видимой колонкой,
 * которая стоит раньше них. Использовать только на начальной регистрации описаний, чтобы пользователи
 * с уже сохранёнными настройками увидели новые колонки, добавленные после их последнего захода.
 * @kind helper
 * @param requestedVisibleColumnIds - список видимых колонок
 * @param columnDefinitions - описания колонок
 * @param defaultVisibleColumnIdsToMergeIn - id колонок, дефолтно видимых; те, кого нет в запрошенном, докинутся по порядку
 * @returns список видимых колонок, включая обязательные в начале
 */
export function normalizeVisibleColumnIds(
    requestedVisibleColumnIds: string[],
    columnDefinitions: GridColumnVisibilityDefinition[],
    defaultVisibleColumnIdsToMergeIn?: readonly string[]
): string[] {
    const knownColumnIds = new Set(columnDefinitions.map((definition) => definition.columnId));
    const requiredColumnIds = columnDefinitions
        .filter((definition) => definition.isRequired)
        .map((definition) => definition.columnId);
    const allowedRequestedIds = requestedVisibleColumnIds.filter((columnId) => knownColumnIds.has(columnId));
    const visibleColumnIds = [...new Set([...requiredColumnIds, ...allowedRequestedIds])];
    const visibleColumnIdSet = new Set(visibleColumnIds);
    const mergedDefaultIds = [
        ...new Set(
            defaultVisibleColumnIdsToMergeIn?.filter(
                (columnId) => knownColumnIds.has(columnId) && !visibleColumnIdSet.has(columnId)
            ) ?? []
        ),
    ];

    const isAvailableColumn = isAvailableColumnFactory(columnDefinitions);

    if (mergedDefaultIds.length === 0) {
        return visibleColumnIds.filter(isAvailableColumn);
    }

    const orderIndexByColumnId = new Map(columnDefinitions.map((definition, index) => [definition.columnId, index]));

    for (const columnId of mergedDefaultIds) {
        insertByColumnDefinitionsOrder(visibleColumnIds, columnId, orderIndexByColumnId, requiredColumnIds.length);
    }

    return visibleColumnIds.filter(isAvailableColumn);
}
